import { supabase } from '@/integrations/supabase/client';
import { APP_REGISTRY, getActiveApps, getAppDescription, type AppDefinition } from '@/config/app-registry';

export interface AppItem {
  id: string;
  name: string;
  display_name?: string;
  short_display_name?: string; // NEW: Short name for mobile
  description: string;
  category: string;
  icon_name: string;
  route: string;
  status: string;
  created_at: string;
  updated_at: string;
  favorite_count: number;
}

export interface AppFavorite {
  id: string;
  user_id: string;
  app_id: string;
  created_at: string;
}

/**
 * Fetch all app items from the registry with favorite counts from database
 */
export async function fetchAppItems() {
  // Get active apps from registry
  const registryApps = getActiveApps();
  
  // Fetch all favorites from database and count them
  const { data: allFavorites, error } = await supabase
    .from('app_favorites')
    .select('app_id');
  
  if (error) {
    console.warn('Error fetching favorite counts:', error);
    // Continue without favorite counts rather than failing
  }
  
  // Create a map of app_id to favorite count
  const favoriteCountMap = new Map<string, number>();
  allFavorites?.forEach(favorite => {
    const currentCount = favoriteCountMap.get(favorite.app_id) || 0;
    favoriteCountMap.set(favorite.app_id, currentCount + 1);
  });
  
  // Transform registry apps to match AppItem interface
  const transformedData: AppItem[] = registryApps.map(app => {
    return {
      id: app.id,
      name: app.name,
      display_name: app.displayName,
      short_display_name: app.shortDisplayName, // NEW: Short name for mobile
      description: getAppDescription(app.id),
      category: app.category,
      icon_name: app.icon,
      route: app.route,
      status: app.status,
      created_at: new Date().toISOString(), // Placeholder
      updated_at: new Date().toISOString(), // Placeholder
      favorite_count: favoriteCountMap.get(app.id) || 0,
    };
  });
  
  // Sort by status priority: active first, then by category
  const sortedData = transformedData.sort((a, b) => {
    const statusPriority = {
      'active': 1,
      'beta': 2,
      'coming_soon': 3
    };
    
    const aPriority = statusPriority[a.status as keyof typeof statusPriority] || 999;
    const bPriority = statusPriority[b.status as keyof typeof statusPriority] || 999;
    
    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }
    
    return (b.favorite_count || 0) - (a.favorite_count || 0);
  });
  
  return sortedData;
}

/**
 * Fetch user favorites for the current user
 */
export async function fetchAppFavorites() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return []; // Return empty array if no user
  }

  const { data, error } = await supabase
    .from('app_favorites')
    .select('*')
    .eq('user_id', user.id);
  
  if (error) {
    console.error('Error fetching app favorites:', error);
    throw error;
  }
  return data as AppFavorite[];
}

/**
 * Toggle favorite status for an app
 */
export async function toggleAppFavorite(appId: string, isFavorited: boolean) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  if (isFavorited) {
    // Remove favorite
    const { error } = await supabase
      .from('app_favorites')
      .delete()
      .eq('app_id', appId)
      .eq('user_id', user.id);
    
    if (error) throw error;
  } else {
    // Add favorite
    const { error } = await supabase
      .from('app_favorites')
      .insert({ 
        app_id: appId,
        user_id: user.id
      });
    
    if (error) throw error;
  }

  return { appId, isFavorited, userId: user.id };
}
